import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { UpdateBillingAddress } from '../../store/actions/customer.actions';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomerInterface } from '../../models/customer.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { AddressInterface } from '../../models/address.interface';
import { EAddressActions, UpdateShippingAddress } from '../../store/actions/address.actions';
import { ConstsHelper } from '../../../../helpers/consts.helper';
import { ConfirmationAddressInterfaceState } from '../../store/state/confirmation-address.state';
import { CheckPasswordInterfaceState } from '../../../shared/store/state/check-password.state';
import { CheckPasswordInterface } from '../../../shared/models/check-password.interface';
import { MfaInterfaceState } from '../../../shared/store/state/mfa.state';
import { UpdateMfaVisibility } from '../../../shared/store/actions/mfa.actions';
import { ExtranetService } from '../../../shared/services/extranet.service';
import { MfaInterface } from '../../../shared/models/mfa.interface';
import { UpdateCheckPasswordVisibility } from '../../../shared/store/actions/check-password.actions';
import { Actions, ofType } from '@ngrx/effects';

@Component({
    selector: 'app-update-address',
    templateUrl: './update-address.component.html',
    styleUrls: ['./update-address.component.css'],
    standalone: false
})
export class UpdateAddressComponent implements OnInit {

    @ViewChild('loaderWrapper', {static: false}) loaderWrapper: ElementRef;

    type = 'adresse-facturation';
    customer: CustomerInterface;
    address: AddressInterface;
    addressFrom: FormGroup;
    addressFromSubmitted: boolean;
    display: string = null;
    civility = 'Mr';
    passwordResponse: CheckPasswordInterface;
    mfaResponse: MfaInterface|null;

    errors: any = {
        lastName: {
            required: `Le nom est obligatoire.`,
            pattern: 'Veuillez saisir un nom sans accent, caractères spéciaux ni chiffres.'
        },
        firstName: {
            required: `Le prénom est obligatoire.`,
            pattern: `Veuillez saisir un prénom sans accent, caractères spéciaux ni chiffres.`
        },
        company: {
            pattern: `Veuillez saisir un nom de société sans accent ni caractères spéciaux.`
        },
        label: {
            required: `Un nom d'adresse est obligatoire.`,
            pattern: `Veuillez saisir un nom d'adresse avec au moins deux caractères et sans utiliser de caractères spéciaux ni accent.`
        },
        street: {
            required: `L'adresse' est obligatoire.`,
            pattern: `Veuillez saisir une adresse avec au moins deux caractères et sans utiliser de caractères spéciaux ni accent.`
        },
        zipCode: {
            required: `Le code postal est obligatoire.`,
            pattern: `Le code postal saisie n'est pas valide.`
        },
        city: {
            required: `La ville est obligatoire.`,
            pattern: `Veuillez saisir un nom de ville sans accent, caractères spéciaux ni chiffres.`
        },
        addressComplement: {
            pattern: `Veuillez saisir un complément d\'adresse avec au moins deux caractères et sans utiliser de caractères spéciaux ni accent.`
        },
        placeName: {
            pattern: `Veuillez saisir un lieu-dit avec au moins deux caractères et sans utiliser de caractères spéciaux ni accent.`
        }
    };

    constructor(
        private formBuilder: FormBuilder,
        private router: Router,
        private activeRoute: ActivatedRoute,
        private store: Store<any>,
        private actions$: Actions,
        private extranetService: ExtranetService
    ) {}

    ngOnInit() {
        this.store.pipe(select(ConstsHelper.REDUCER_CHECK_PASSWORD)).subscribe((response: CheckPasswordInterfaceState) => {
            if (response.result && response.result.isValid === true && (response.result.page === 'adresse-facturation' || response.result.page === 'adresse-livraison')) {
                this.passwordResponse = response.result;
                if (response.result.page === 'adresse-facturation') {
                    this.customer = this.passwordResponse.object;
                    this.initBillingAddressFrom();
                } else if (response.result.page === 'adresse-livraison') {
                    this.address = this.passwordResponse.object;
                    this.initShippingAddressFrom();
                }
            }
        });

        this.store.pipe(select(ConstsHelper.REDUCER_CONFIRMATION_ADDRESS)).subscribe( (result: ConfirmationAddressInterfaceState) => {
            if (null != result.visibility) {
                this.display = result.visibility ? 'block' : 'none';
                if (this.display === 'block' && this.extranetService.getSupportUser() == null) {
                    this.mfaResponse.visibility = false;
                    this.store.dispatch(new UpdateMfaVisibility(this.mfaResponse));
                }
            }
        });

        this.store.pipe(select(ConstsHelper.REDUCER_MFA)).subscribe((response: MfaInterfaceState) => {
            if (response.result != null) {
                this.mfaResponse = response.result;
            }
        });

        this.actions$.pipe(ofType(EAddressActions.UpdateAddressSuccess)).subscribe((result: any) => {
            window.location.reload();
        });

        this.mfaResponse = null;
        if (!this.passwordResponse || this.passwordResponse.isValid === false) {
            this.router.navigate(['compte/adresses']);
        }
        this.display = null;
        if (
            this.activeRoute.snapshot.params.type &&
            ['adresse-facturation', 'adresse-livraison'].indexOf(this.activeRoute.snapshot.params.type) !== -1
        ) {
            this.type = this.activeRoute.snapshot.params.type;
        }
        this.addressFromSubmitted = false;
    }

    /**
     * Init shipping address form
     */
    initShippingAddressFrom() {
        this.civility = this.address.civility !== '' ? this.address.civility : 'Mr';
        this.addressFrom = this.formBuilder.group({});
        this.addressFrom.addControl('firstName', this.formBuilder.control(this.address.firstName, [Validators.required, Validators.pattern(ConstsHelper.firstNamePattern)]));
        this.addressFrom.addControl('lastName', this.formBuilder.control(this.address.lastName, [Validators.required, Validators.pattern(ConstsHelper.lastNamePattern)]));
        this.addressFrom.addControl('company', this.formBuilder.control(this.address.company, [Validators.pattern(ConstsHelper.companyPattern)]));
        this.addressFrom.addControl('label', this.formBuilder.control(this.address.label, [Validators.required, Validators.pattern(ConstsHelper.addressPattern)]));
        this.addressFrom.addControl('street', this.formBuilder.control(this.address.street, [Validators.required, Validators.pattern(ConstsHelper.addressPattern)]));
        this.addressFrom.addControl('addressComplement', this.formBuilder.control(this.address.addressComplement, [Validators.pattern(ConstsHelper.addressPattern)]));
        this.addressFrom.addControl('zipCode', this.formBuilder.control(this.address.zipCode, [Validators.required, Validators.pattern(ConstsHelper.zipCodePattern)]));
        this.addressFrom.addControl('city', this.formBuilder.control(this.address.city, [Validators.required, Validators.pattern(ConstsHelper.cityPattern)]));
        this.addressFrom.addControl('placeName', this.formBuilder.control(this.address.placeName, [Validators.pattern(ConstsHelper.addressPattern)]));
    }

    /**
     * Init billing address form
     */
    initBillingAddressFrom() {
        this.addressFrom = this.formBuilder.group({});
        this.addressFrom.addControl('street', this.formBuilder.control(this.customer.street, [Validators.required, Validators.pattern(ConstsHelper.addressPattern)]));
        this.addressFrom.addControl('addressComplement', this.formBuilder.control(this.customer.addressComplement, [Validators.pattern(ConstsHelper.addressPattern)]));
        this.addressFrom.addControl('zipCode', this.formBuilder.control(this.customer.zipCode, [Validators.required, Validators.pattern(ConstsHelper.zipCodePattern)]));
        this.addressFrom.addControl('city', this.formBuilder.control(this.customer.city, [Validators.required, Validators.pattern(ConstsHelper.cityPattern)]));
        this.addressFrom.addControl('placeName', this.formBuilder.control(this.customer.placeName, [Validators.pattern(ConstsHelper.addressPattern)]));
    }

    /**
     * Save data
     */
    save() {
        this.addressFromSubmitted = true;
        if (this.addressFrom.valid) {
            if (this.type === 'adresse-facturation') {
                this.saveBillingAddress();
            } else {
                this.saveShippingAddress(false);
            }
            this.addressFromSubmitted = false;
        }
    }

    goToAddressPage() {
        this.store.dispatch(new UpdateCheckPasswordVisibility({visibility: false, isValid: false, password: null, page: null}));
        this.router.navigate(['/compte/adresses']);
    }

    byPassGoogleCheckAddressApi() {
        this.display = null;
        this.saveShippingAddress(true);
    }

    closeModal() {
        this.display = null;
    }

    getError(formControlName: string): string {
        let errorMsg = '';
        if (this.addressFrom.controls[formControlName].invalid) {
            Object.keys(this.addressFrom.controls[formControlName].errors).map(
                key => {
                    errorMsg = this.errors[formControlName][key];
                }
            );
        }
        return errorMsg;
    }

    private saveShippingAddress(byPassGoogleCheck: boolean) {
        this.address.civility = this.civility;
        this.address.firstName = this.addressFrom.get('firstName').value;
        this.address.lastName = this.addressFrom.get('lastName').value;
        this.address.company = this.addressFrom.get('company').value;
        this.address.street = this.addressFrom.get('street').value;
        this.address.addressComplement = this.addressFrom.get('addressComplement').value;
        this.address.zipCode = this.addressFrom.get('zipCode').value;
        this.address.city = this.addressFrom.get('city').value;
        this.address.placeName = this.addressFrom.get('placeName').value;
        this.address.label = this.addressFrom.get('label').value;
        this.address.byPassGoogleCheck = true; //byPassGoogleCheck;
        this.address.password = this.passwordResponse.password;
        document.getElementById('wrapper-content').scrollTop = 0;
        if (this.extranetService.getSupportUser()) {
            this.store.dispatch(new UpdateShippingAddress(this.address));
        } else if (byPassGoogleCheck) {
            this.store.dispatch(new UpdateShippingAddress(this.address, this.mfaResponse));
        } else {
            this.store.dispatch(
                new UpdateMfaVisibility(
                    {
                        visibility: true,
                        action: 'change_shipping_address',
                        step: (this.mfaResponse !== null) ? this.mfaResponse.step : 1,
                        destination: (this.mfaResponse !== null) ? this.mfaResponse.destination : null,
                        code: (this.mfaResponse !== null) ? this.mfaResponse.code : null,
                        errorMessage: (this.mfaResponse !== null) ? this.mfaResponse.errorMessage : null,
                        effect: UpdateShippingAddress,
                        effectData: this.address
                    }
                )
            );
        }
    }

    private saveBillingAddress() {
        this.customer.street = this.addressFrom.get('street').value;
        this.customer.addressComplement = this.addressFrom.get('addressComplement').value;
        this.customer.zipCode = this.addressFrom.get('zipCode').value;
        this.customer.city = this.addressFrom.get('city').value;
        this.customer.placeName = this.addressFrom.get('placeName').value;
        this.customer.password = this.passwordResponse.password;
        if (this.extranetService.getSupportUser()) {
            this.store.dispatch(new UpdateBillingAddress(this.customer));
        } else {
            this.store.dispatch(
                new UpdateMfaVisibility(
                    {
                        visibility: true,
                        action: 'change_billing_address',
                        step: 1,
                        destination: null,
                        code: null,
                        errorMessage: null,
                        effect: UpdateBillingAddress,
                        effectData: this.customer
                    }
                )
            );
        }
    }
}
